import {
  type NotificationsFeatureOptions,
  type ShowDesktopNotificationFn,
} from '@/features/notifications'
import { NotificationsApi } from '@/features/notifications/api/notifications-api'
import { NotificationsPageViewModel } from '@/features/notifications/NotificationsPageViewModel'
import { NotificationsSidebarViewModel } from '@/features/notifications/NotificationsSidebarViewModel'
import { env } from '@/infra/env'
import { defer, distinctUntilChanged, first, merge, switchMap } from 'rxjs'
import { toStream } from '@/utils/mobx-util'
import { shareLatest } from '@/utils/rx-util'
import { DesktopNotificationStore } from './DesktopNotificationStore'
import { NotificationPreferencesStore } from './NotificationPreferencesStore'
import { notificationsToSystemNotifications } from './notifications-to-system-notifications'
import { NotificationStore } from './NotificationStore'

export function InternalNotificationsFeature(
  options: NotificationsFeatureOptions,
) {
  const notificationsApi = new NotificationsApi({
    baseURL: env.NOTIFICATION_API,
    token$: options.token$,
  })

  const notificationStore = new NotificationStore(
    notificationsApi,
    options.primeMember,
  )

  const notificationPreferencesStore = new NotificationPreferencesStore(
    notificationsApi,
  )

  const desktopNotificationStore = new DesktopNotificationStore(
    options.currentUser$,
  )

  const notificationsSidebarVm = new NotificationsSidebarViewModel(
    notificationStore,
    options.getCurrentUserWorkspaceMemberships,
    options.getTime,
    options.job,
    options.createAlertErrorHandler,
    options.createAlertRetryWrapper,
  )

  const notificationsPageVm = new NotificationsPageViewModel(
    notificationsSidebarVm,
    options.fetchJob,
    options.navigate,
  )

  // Subscribing to the unread count fetches the first page of notifications
  // through the sidebar's list, so the list is already loaded when opened.
  const unreadCount$ = options.currentMember$.pipe(
    first(),
    // A failed fetch still counts what realtime updates bring in.
    switchMap(() =>
      defer(() =>
        notificationsSidebarVm.list.fetchNotifications().catch(() => undefined),
      ),
    ),
    switchMap(() => toStream(() => notificationStore.unreadCount, true)),
    distinctUntilChanged(),
    shareLatest(),
  )

  const showDesktopNotification: ShowDesktopNotificationFn =
    desktopNotificationStore.show

  merge(
    notificationsToSystemNotifications(
      notificationStore.newNotifications$,
      options.navigate,
    ),
    options.systemNotifications$,
  ).subscribe(showDesktopNotification)

  return {
    // Internal
    notificationStore,
    notificationPreferencesStore,
    desktopNotificationStore,
    notificationsSidebarVm,
    notificationsPageVm,
    unreadCount$,
    showDesktopNotification,
  }
}
